/**
 * Posts API client (T2.10a / REQ-75) — text posts on the trips API.
 * Feed v1 = the server's pull-merge of followed authors (staged D-27);
 * recommendations arrive later as a second, labeled candidate source.
 */

import { getTokensFromCache } from '../auth/utils/tokenCache';

const API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_TRIPS_ENDPOINT;

export interface PostMediaView {
  mediaId: string;
  status: 'live' | 'pending' | string;
  url?: string;      // expiring full-size URL (live only)
  thumbUrl?: string; // expiring thumbnail URL (live only)
}

export interface ServerPost {
  postId: string;
  authorId: string;
  text: string;
  createdAt: string;
  visibility: 'followers';
  media?: PostMediaView[];
  /** Public comment count (T2.10d) — server-enriched on feed/list reads. */
  commentCount?: number;
}

export interface PostComment {
  commentId: string;
  postId: string;
  /** The COMMENTER's user id (resolve to a profile for display). */
  authorId: string;
  text: string;
  createdAt: string;
}

export interface PostPage {
  posts: ServerPost[];
  nextBefore?: string;
}

const getAccessToken = (): string | null => {
  try {
    const tokens = getTokensFromCache();
    return tokens?.idToken || null;
  } catch {
    return null;
  }
};

const request = async (path: string, init?: RequestInit): Promise<any> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }
  const accessToken = getAccessToken();
  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const response = await fetch(`${API_ENDPOINT}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // No Bearer prefix - API Gateway uses Cognito authorizer
      ...(init?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => '');
    console.error(`❌ posts API ${path} failed:`, { status: response.status, errorText });
    const error: any = new Error(`Request failed: ${response.statusText}`);
    error.status = response.status;
    throw error;
  }
  return response.json();
};

export const createPost = async (text: string): Promise<ServerPost> => {
  const data = await request('/posts', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
  return data.post;
};

export const getFeed = async (before?: string): Promise<PostPage> => {
  const query = before ? `?before=${encodeURIComponent(before)}` : '';
  const data = await request(`/feed${query}`);
  return { posts: data.posts || [], nextBefore: data.nextBefore };
};

export const getMyPosts = async (before?: string): Promise<PostPage> => {
  const query = before ? `?before=${encodeURIComponent(before)}` : '';
  const data = await request(`/posts/mine${query}`);
  return { posts: data.posts || [], nextBefore: data.nextBefore };
};

export const deletePost = async (postId: string): Promise<void> => {
  await request(`/post/${encodeURIComponent(postId)}`, { method: 'DELETE' });
};

export const MAX_POST_PHOTOS = 5;

export interface PostMediaUploadTarget {
  mediaId: string;
  upload: { url: string; fields: Record<string, string> };
}

/**
 * Presigned direct-to-S3 upload slots for photos on the caller's post —
 * ONE request mints up to `count` slots (burst control: auth + cap check are
 * paid once, uploads then hit S3 directly in parallel).
 */
export const presignPostMedia = async (
  postId: string,
  count: number,
): Promise<PostMediaUploadTarget[]> => {
  const data = await request(`/post/${encodeURIComponent(postId)}/media/presign`, {
    method: 'POST',
    body: JSON.stringify({ count }),
  });
  return Array.isArray(data.media) ? data.media : [data.media];
};

export interface UserPostsResult extends PostPage {
  /** True when the server said 403 — the viewer must follow the author first. */
  forbidden?: boolean;
}

export const getUserPosts = async (userId: string, before?: string): Promise<UserPostsResult> => {
  const query = before ? `?before=${encodeURIComponent(before)}` : '';
  try {
    const data = await request(`/user/${encodeURIComponent(userId)}/posts${query}`);
    return { posts: data.posts || [], nextBefore: data.nextBefore };
  } catch (error: any) {
    if (error?.status === 403) return { posts: [], forbidden: true };
    throw error;
  }
};

export interface CommentPage {
  comments: PostComment[];
  nextBefore?: string;
}

/**
 * A post's comment thread (oldest-first). The post is author-keyed server-side,
 * so the post's author id is passed for the visibility check — the caller holds
 * it from the feed (post.author.id).
 */
export const getComments = async (
  postId: string,
  postAuthorId: string,
  before?: string,
): Promise<CommentPage> => {
  const params = new URLSearchParams({ authorId: postAuthorId });
  if (before) params.set('before', before);
  const data = await request(`/post/${encodeURIComponent(postId)}/comments?${params.toString()}`);
  return { comments: data.comments || [], nextBefore: data.nextBefore };
};

/** Comment on any post the viewer can see (T2.10d). */
export const createComment = async (
  postId: string,
  postAuthorId: string,
  text: string,
): Promise<PostComment> => {
  const data = await request(`/post/${encodeURIComponent(postId)}/comment`, {
    method: 'POST',
    body: JSON.stringify({ authorId: postAuthorId, text }),
  });
  return data.comment;
};
